import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { platformFor } from '@deepdive-icons';
import { Badge } from '../../../atoms/Badge/Badge';
import { Button } from '../../../atoms/Button/Button';
import { Checkbox } from '../../../atoms/Checkbox/Checkbox';
import { Icon } from '../../../atoms/Icon/Icon';
import { PlatformIcon } from '../../../atoms/PlatformIcon/PlatformIcon';
import { InputField } from '../../../molecules/InputField/InputField';
import { LoadingState } from '../../../molecules/LoadingState/LoadingState';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { Select, type SelectOption } from '../../../molecules/Select/Select';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import type { TableHeaderSort } from '../../../molecules/TableHeaderLabel/TableHeaderLabel';
import { Tabs } from '../../../molecules/Tabs/Tabs';
import { TextareaField } from '../../../molecules/TextareaField/TextareaField';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { ExportModal } from '../../modals/ExportModal/ExportModal';
import styles from './QuickSearchWorkspace.module.css';

export type QuickSearchResultView = {
  id: string;
  text: string;
  reactions: string;
  reactionsValue: number;
  comments: string;
  commentsValue: number;
  shares: string;
  sharesValue: number;
  postedAt: string;
  postedAtValue: number;
  processing: boolean;
  url: string;
};

export type QuickSearchHistoryView = {
  id: string;
  query: string;
  channelKey: string;
  channel: string;
  range: string;
  rangeDays: number | null;
  ranAt: string;
  ranAtValue: number;
};

export type QuickSearchWorkspaceProps = {
  detail?: boolean;
  phase: 'idle' | 'searching' | 'results' | 'error';
  tab: 'new' | 'history';
  channel: string;
  channelOptions: SelectOption[];
  maxResults: string;
  terms: string[];
  mode: 'keyword' | 'hashtag';
  period: string;
  timeframe: string;
  sort: 'liked' | 'commented' | 'shared' | 'recent';
  country: string;
  results: QuickSearchResultView[];
  resultSetKey: string;
  resultOrder: 'liked' | 'commented' | 'shared' | 'recent';
  history: QuickSearchHistoryView[];
  errorMessage?: string;
  onTabChange: (tab: 'new' | 'history') => void;
  onChannelChange: (channel: string) => void;
  onMaxResultsChange: (value: string) => void;
  onTermsChange: (terms: string[]) => void;
  onModeChange: (mode: 'keyword' | 'hashtag') => void;
  onPeriodChange: (period: string) => void;
  onTimeframeChange: (timeframe: string) => void;
  onSortChange: (sort: 'liked' | 'commented' | 'shared' | 'recent') => void;
  onCountryChange: (country: string) => void;
  onSearch: () => void;
  onReset: () => void;
  onAdd: (ids: string[]) => Promise<void>;
  onDeleteHistory: (id: string) => void;
  onViewResult: (result: QuickSearchResultView) => void;
  historyHref: (id: string) => string;
};

const PAGE_SIZE = 10;

type ResultSortKey = 'reactionsValue' | 'commentsValue' | 'sharesValue' | 'postedAtValue';
type HistorySortKey = 'channel' | 'range' | 'ranAtValue';
type SortState<Key> = { key: Key; direction: 'asc' | 'desc' };

export function QuickSearchWorkspace(props: QuickSearchWorkspaceProps) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [added, setAdded] = useState<ReadonlySet<string>>(() => new Set());
  const [page, setPage] = useState(1);
  const [exportOpen, setExportOpen] = useState(false);
  const [resultSort, setResultSort] = useState<SortState<ResultSortKey>>({
    key: 'postedAtValue',
    direction: 'desc',
  });
  const [historySort, setHistorySort] = useState<SortState<HistorySortKey>>({
    key: 'ranAtValue',
    direction: 'desc',
  });
  useEffect(() => {
    const key: ResultSortKey =
      props.resultOrder === 'liked'
        ? 'reactionsValue'
        : props.resultOrder === 'commented'
          ? 'commentsValue'
          : props.resultOrder === 'shared'
            ? 'sharesValue'
            : 'postedAtValue';
    setResultSort({ key, direction: 'desc' });
    setSelected(new Set());
    setPage(1);
  }, [props.resultOrder, props.resultSetKey]);
  const sortedResults = useMemo(() => {
    const rows = [...props.results].sort((a, b) => a[resultSort.key] - b[resultSort.key]);
    return resultSort.direction === 'desc' ? rows.reverse() : rows;
  }, [props.results, resultSort]);
  const sortedHistory = useMemo(() => {
    const rows = [...props.history].sort((a, b) => {
      if (historySort.key === 'channel' || historySort.key === 'range') {
        return a[historySort.key].localeCompare(b[historySort.key]);
      }
      return a.ranAtValue - b.ranAtValue;
    });
    return historySort.direction === 'desc' ? rows.reverse() : rows;
  }, [historySort, props.history]);
  const pageCount = Math.max(1, Math.ceil(sortedResults.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visible = sortedResults.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const isTicked = (id: string) => selected.has(id) || added.has(id);
  const allVisibleTicked = visible.length > 0 && visible.every((result) => isTicked(result.id));
  const someVisibleTicked = visible.some((result) => isTicked(result.id));
  const everyVisibleAdded = visible.length > 0 && visible.every((result) => added.has(result.id));
  const selectedPlatform = platformFor(props.channel);
  const locked = props.tab === 'history' || props.phase !== 'idle';
  const isTikTok = props.channel === 'tiktok';
  const hashtagMode = isTikTok && props.mode === 'hashtag';

  const resultSortFor = (key: ResultSortKey): TableHeaderSort =>
    resultSort.key === key ? resultSort.direction : 'selector';
  const onResultSort = (key: ResultSortKey) => () =>
    setResultSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  const historySortFor = (key: HistorySortKey): TableHeaderSort =>
    historySort.key === key ? historySort.direction : 'selector';
  const onHistorySort = (key: HistorySortKey) => () =>
    setHistorySort((current) => ({
      key,
      direction:
        current.key === key
          ? current.direction === 'desc'
            ? 'asc'
            : 'desc'
          : key === 'ranAtValue'
            ? 'desc'
            : 'asc',
    }));

  const resultColumns: TableColumn[] = [
    {
      key: 'conversation',
      header: 'Conversation',
      headerCheckbox: (
        <Checkbox
          checked={allVisibleTicked}
          indeterminate={!allVisibleTicked && someVisibleTicked}
          disabled={everyVisibleAdded}
          aria-label="Select every result on this page"
          onChange={(checked) => {
            setSelected((current) => {
              const next = new Set(current);
              for (const result of visible) {
                if (added.has(result.id)) continue;
                if (checked) next.add(result.id);
                else next.delete(result.id);
              }
              return next;
            });
          }}
        />
      ),
    },
    {
      key: 'reactions',
      header: 'Reactions',
      width: '97px',
      sort: resultSortFor('reactionsValue'),
      onSort: onResultSort('reactionsValue'),
    },
    {
      key: 'comments',
      header: 'Comments',
      width: '103px',
      sort: resultSortFor('commentsValue'),
      onSort: onResultSort('commentsValue'),
    },
    {
      key: 'shares',
      header: 'Shares',
      width: '81px',
      sort: resultSortFor('sharesValue'),
      onSort: onResultSort('sharesValue'),
    },
    {
      key: 'posted',
      header: 'Date posted',
      width: '110px',
      sort: resultSortFor('postedAtValue'),
      onSort: onResultSort('postedAtValue'),
    },
    { key: 'status', header: 'Status', cellType: 'badge', width: added.size > 0 ? '84px' : '60px' },
    { key: 'actions', header: 'Actions', cellType: 'actionButtons', width: '77px' },
  ];

  const historyColumns: TableColumn[] = [
    { key: 'query', header: 'Query' },
    {
      key: 'channel',
      header: 'Channel',
      cellType: 'actionIcons',
      width: '113px',
      sort: historySortFor('channel'),
      onSort: onHistorySort('channel'),
    },
    {
      key: 'range',
      header: 'Date range',
      width: '103px',
      sort: historySortFor('range'),
      onSort: onHistorySort('range'),
    },
    {
      key: 'posted',
      header: 'Date searched',
      width: '110px',
      sort: historySortFor('ranAtValue'),
      onSort: onHistorySort('ranAtValue'),
    },
    { key: 'actions', cellType: 'actionButtons', width: '114px' },
  ];

  const resultRows: TableRowData[] = visible.map((result) => {
    const processing = result.processing || added.has(result.id);
    return {
      key: result.id,
      selected: selected.has(result.id),
      cells: {
        conversation: {
          text: result.text,
          leadAction: (
            <Checkbox
              checked={isTicked(result.id)}
              disabled={processing}
              aria-label={`Select ${result.text}`}
              onChange={(checked) => {
                setSelected((current) => {
                  const next = new Set(current);
                  if (checked) next.add(result.id);
                  else next.delete(result.id);
                  return next;
                });
              }}
            />
          ),
        },
        reactions: { text: result.reactions },
        comments: { text: result.comments },
        shares: { text: result.shares },
        posted: { text: result.postedAt },
        status: processing ? { content: <Badge type="positive">Processing</Badge> } : { text: '-' },
        actions: {
          content: (
            <Button
              hierarchy="ghostColor"
              iconTrailing={<Icon name="arrowUpRight" />}
              aria-label={`View ${result.text}`}
              onClick={() => props.onViewResult(result)}
            >
              View
            </Button>
          ),
        },
      },
    };
  });

  const historyRows: TableRowData[] = sortedHistory.map((entry) => ({
    key: entry.id,
    cells: {
      query: { text: entry.query },
      channel: {
        content: platformFor(entry.channelKey) ? (
          <span className={styles.channelCell}>
            <PlatformIcon name={platformFor(entry.channelKey)!.name} label={entry.channel} />
          </span>
        ) : (
          entry.channel
        ),
      },
      range: { text: entry.range },
      posted: { text: entry.ranAt },
      actions: {
        content: (
          <div className={styles.actions}>
            <Button
              hierarchy="ghostGrey"
              iconLeading={<Icon name="deleteBin" />}
              aria-label={`Delete ${entry.query}`}
              onClick={() => props.onDeleteHistory(entry.id)}
            />
            <Button
              hierarchy="ghostColor"
              href={props.historyHref(entry.id)}
              iconTrailing={<Icon name="chevronRight" />}
              aria-label={`Details of the search for ${entry.query}`}
            >
              Details
            </Button>
          </div>
        ),
      },
    },
  }));

  const exportDescription =
    selected.size > 0
      ? `The export covers the ${selected.size} selected ${selected.size === 1 ? 'result' : 'results'} of this search.`
      : `The export covers all ${props.results.length} results of this search.`;

  const resultsTable = (
    <Table
      label="Search results"
      columns={resultColumns}
      rows={resultRows}
      emptyLabel="No posts matched those terms on this channel."
      header={
        <TableHeader
          title="Search results"
          badge={String(props.results.length)}
          helpText="Posts on the chosen channel matching any of your search terms."
          actions={
            <div className={styles.actions}>
              <Button
                hierarchy="tertiary"
                iconLeading={<Icon name="export" />}
                disabled={props.results.length === 0}
                onClick={() => setExportOpen(true)}
                aria-haspopup="dialog"
              >
                Export
              </Button>
              <Button
                hierarchy="secondary"
                iconLeading={<Icon name="add" />}
                disabled={selected.size === 0}
                onClick={() => {
                  const ids = [...selected];
                  void props.onAdd(ids).then(() => {
                    setAdded((current) => new Set([...current, ...ids]));
                    setSelected(new Set());
                  });
                }}
              >
                Add to dashboard
              </Button>
            </div>
          }
        />
      }
      pagination={
        sortedResults.length > PAGE_SIZE ? (
          <Pagination
            page={currentPage}
            pageCount={pageCount}
            onPageChange={setPage}
            label="Search results pages"
          />
        ) : undefined
      }
    />
  );

  if (props.detail) {
    return (
      <>
        {props.phase === 'searching' ? (
          <div className={styles.detailState} aria-busy="true">
            <LoadingState pattern="orbit" tone="accent" showLabel={false} showElapsed={false} />
          </div>
        ) : props.phase === 'error' ? (
          <div className={styles.detailState} role="alert">
            <p>{props.errorMessage ?? 'The saved search could not be loaded.'}</p>
          </div>
        ) : (
          resultsTable
        )}
        <ExportModal
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          description={exportDescription}
        />
      </>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    props.onSearch();
  };

  return (
    <>
      <div className={styles.module}>
        <aside className={styles.formPane}>
          <p className={styles.blurb}>
            Search by keyword, hashtag, page or profile URL, or a specific post link. You can enter
            up to 10 search terms at once.
          </p>
          <div className={styles.pane}>
            <Tabs
              label="Quick search"
              items={[
                { value: 'new', label: 'New search', icon: <Icon name="search" /> },
                { value: 'history', label: 'History', icon: <Icon name="chatHistory" /> },
              ]}
              value={props.tab}
              onChange={(value) => props.onTabChange(value === 'history' ? 'history' : 'new')}
            />
            <form className={styles.form} onSubmit={submit}>
              <Select
                type="iconLeading"
                label="Where do you want to search?"
                required={isTikTok}
                hint={isTikTok ? undefined : 'This is a hint text to help user.'}
                options={props.channelOptions}
                value={props.channel}
                onValueChange={props.onChannelChange}
                icon={selectedPlatform ? <PlatformIcon name={selectedPlatform.name} /> : undefined}
                disabled={locked}
              />
              {isTikTok && (
                <Select
                  label="How do you want to search?"
                  required
                  options={[
                    { value: 'keyword', label: 'By keyword' },
                    { value: 'hashtag', label: 'By hashtags' },
                  ]}
                  value={props.mode}
                  onValueChange={(value) =>
                    props.onModeChange(value === 'hashtag' ? 'hashtag' : 'keyword')
                  }
                  disabled={locked}
                />
              )}
              {!isTikTok && (
                <InputField
                  label="Max results"
                  required
                  hint="Higher numbers may take longer"
                  inputMode="numeric"
                  value={props.maxResults}
                  onValueChange={props.onMaxResultsChange}
                  disabled={locked}
                />
              )}
              <TextareaField
                type="tags"
                label={
                  !isTikTok
                    ? 'What would you like to search?'
                    : hashtagMode
                      ? 'What hashtags would you like to search?'
                      : 'What keywords would you like to search?'
                }
                required
                hint="You can enter up to 10 entries"
                placeholder="Separate entries with tab..."
                tags={props.terms}
                onTagsChange={(terms) => props.onTermsChange(terms.slice(0, 10))}
                resizable={false}
                disabled={locked}
              />
              {isTikTok && !hashtagMode && (
                <>
                  <div className={styles.fieldRow}>
                    <Select
                      label="Time period"
                      required
                      options={[
                        { value: '1', label: 'Last 24 hours' },
                        { value: '7', label: 'Last 7 days' },
                        { value: '30', label: 'Last 30 days' },
                        { value: '90', label: 'Last 90 days' },
                      ]}
                      value={props.period}
                      onValueChange={props.onPeriodChange}
                      disabled={locked}
                    />
                    <Select
                      label="Sort by"
                      required
                      options={[
                        { value: 'liked', label: 'Most liked' },
                        { value: 'commented', label: 'Most commented' },
                        { value: 'shared', label: 'Most shared' },
                        { value: 'recent', label: 'Most recent' },
                      ]}
                      value={props.sort}
                      onValueChange={(value) =>
                        props.onSortChange(
                          value === 'commented' || value === 'shared' || value === 'recent'
                            ? value
                            : 'liked',
                        )
                      }
                      disabled={locked}
                    />
                  </div>
                  <Select
                    label="Country"
                    required
                    options={['United States', 'United Kingdom', 'Canada', 'Australia'].map(
                      (value) => ({ value, label: value }),
                    )}
                    value={props.country}
                    onValueChange={props.onCountryChange}
                    disabled={locked}
                  />
                </>
              )}
              {hashtagMode && (
                <InputField
                  label="Timeframe"
                  required
                  hint="Number of past days to include in the search"
                  inputMode="numeric"
                  value={props.timeframe}
                  onValueChange={props.onTimeframeChange}
                  disabled={locked}
                />
              )}
              {isTikTok && (
                <InputField
                  label="Max results"
                  required
                  hint="Higher numbers may take longer"
                  inputMode="numeric"
                  value={props.maxResults}
                  onValueChange={props.onMaxResultsChange}
                  disabled={locked}
                />
              )}
              {props.phase === 'results' ? (
                <Button
                  className={styles.submit}
                  hierarchy="tertiary"
                  iconLeading={<Icon name="revert" />}
                  onClick={props.onReset}
                >
                  Reset search
                </Button>
              ) : (
                <Button
                  className={styles.submit}
                  type="submit"
                  disabled={
                    props.tab === 'history' ||
                    props.phase === 'searching' ||
                    props.terms.length === 0 ||
                    Number(props.maxResults) < 1
                  }
                >
                  {props.phase === 'searching' ? 'Searching...' : 'Search'}
                </Button>
              )}
            </form>
          </div>
        </aside>
        <section className={styles.resultsPane} aria-busy={props.phase === 'searching'}>
          {props.tab === 'history' ? (
            <Table
              className={styles.aligned}
              label="Search history"
              columns={historyColumns}
              rows={historyRows}
              emptyLabel="No searches yet."
              header={
                <TableHeader
                  title="Search history"
                  badge={String(props.history.length)}
                  helpText="Previous searches run in this workspace, listed newest first with the query, channel, date range and search date."
                />
              }
            />
          ) : props.phase === 'searching' ? (
            <div className={styles.centered}>
              <LoadingState pattern="orbit" tone="accent" showLabel={false} showElapsed={false} />
            </div>
          ) : props.phase === 'results' ? (
            <div className={styles.aligned}>{resultsTable}</div>
          ) : props.phase === 'error' ? (
            <div className={styles.centered} role="alert">
              <p>{props.errorMessage ?? 'The search could not be completed.'}</p>
              <Button hierarchy="secondary" onClick={props.onReset}>
                Try again
              </Button>
            </div>
          ) : (
            <div className={styles.centered}>
              <Icon name="search" className={styles.emptyIcon} />
              <p>Results will appear here once you run a search.</p>
            </div>
          )}
        </section>
      </div>
      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        description={exportDescription}
      />
    </>
  );
}
